/**
 * Schema Import Wizard
 *
 * AI-assisted FormSchema generation from natural language descriptions.
 * Adapted from SchemaImportDemo for production use.
 *
 * Flow:
 *   1. User describes what form they need (natural language)
 *   2. Backend LLM analyzes → suggests subgraph, entity type, pattern, compliance
 *   3. User reviews pattern template preview
 *   4. Backend generates FormSchema from pattern template
 *
 * Uses existing backend infrastructure:
 *   - analyzeSchemaIntent query (LLM-powered)
 *   - patternTemplate query (preview)
 *   - generateFormSchema mutation (creation)
 */

import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Code,
  Divider,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Stepper,
  Text,
  Textarea,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCheck,
  IconDatabase,
  IconRobot,
  IconSparkles,
} from '@tabler/icons-react';
import { gql } from '@apollo/client';
// eslint-disable-next-line no-restricted-imports -- Library-level component needs Apollo
import { useLazyQuery, useMutation } from '@apollo/client/react';

// =============================================================================
// GRAPHQL OPERATIONS
// =============================================================================

const ANALYZE_SCHEMA_INTENT = gql`
  query AnalyzeSchemaIntent($description: String!) {
    analyzeSchemaIntent(description: $description) {
      subgraph
      entityType
      suggestedPattern
      confidence
      complianceFrameworks
      reasoning
    }
  }
`;

const PATTERN_TEMPLATE = gql`
  query PatternTemplate($patternId: PatternId!) {
    patternTemplate(patternId: $patternId) {
      patternId
      name
      description
      sections {
        title
        description
        fields {
          fieldName
          fieldType
          label
          required
          width
          order
        }
      }
    }
  }
`;

const GENERATE_FORM_SCHEMA = gql`
  mutation GenerateFormSchema(
    $subgraph: String!
    $entityType: String!
    $patternId: PatternId!
    $config: PatternConfigInput
  ) {
    generateFormSchema(
      subgraph: $subgraph
      entityType: $entityType
      patternId: $patternId
      config: $config
    ) {
      subgraph
      entityType
      version
      displayName
      fields {
        name
        fieldType
        label
        required
      }
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

interface SchemaIntentAnalysis {
  subgraph: string;
  entityType: string;
  suggestedPattern: string;
  confidence: number;
  complianceFrameworks: string[];
  reasoning: string;
}

interface PatternSection {
  title: string;
  description: string;
  fields: Array<{
    fieldName: string;
    fieldType: string;
    label: string;
    required: boolean;
    width?: string;
    order: number;
  }>;
}

interface PatternTemplateData {
  patternId: string;
  name: string;
  description: string;
  sections: PatternSection[];
}

export interface SchemaImportWizardProps {
  /** Called when schema generation completes successfully */
  onComplete?: (result: { subgraph: string; entityType: string }) => void;
  /** Called when user cancels */
  onCancel?: () => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function SchemaImportWizard({ onComplete, onCancel }: SchemaImportWizardProps) {
  const [activeStep, setActiveStep] = useState(0);
  const [description, setDescription] = useState('');
  const [analysis, setAnalysis] = useState<SchemaIntentAnalysis | null>(null);
  const [template, setTemplate] = useState<PatternTemplateData | null>(null);

  // GraphQL operations
  const [analyzeIntent, { loading: analyzing }] = useLazyQuery<Record<string, any>>(ANALYZE_SCHEMA_INTENT);
  const [loadTemplate, { loading: loadingTemplate }] = useLazyQuery<Record<string, any>>(PATTERN_TEMPLATE);
  const [generateSchema, { loading: generating }] = useMutation<Record<string, any>>(GENERATE_FORM_SCHEMA);

  // Handlers
  const handleAnalyze = async () => {
    if (!description.trim()) return;
    const { data } = await analyzeIntent({ variables: { description } });
    if (data?.analyzeSchemaIntent) {
      const result = data.analyzeSchemaIntent as SchemaIntentAnalysis;
      setAnalysis(result);
      setActiveStep(1);
      if (result.suggestedPattern) {
        const { data: tplData } = await loadTemplate({
          variables: { patternId: result.suggestedPattern },
        });
        if (tplData?.patternTemplate) {
          setTemplate(tplData.patternTemplate as PatternTemplateData);
        }
      }
    }
  };

  const handleGenerate = async () => {
    if (!analysis) return;
    const { data } = await generateSchema({
      variables: {
        subgraph: analysis.subgraph,
        entityType: analysis.entityType,
        patternId: analysis.suggestedPattern,
      },
    });
    if (data?.generateFormSchema) {
      setActiveStep(3);
      onComplete?.({ subgraph: data.generateFormSchema.subgraph, entityType: data.generateFormSchema.entityType });
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setDescription('');
    setAnalysis(null);
    setTemplate(null);
  };

  const totalFields = template?.sections.reduce(
    (sum, s) => sum + s.fields.length,
    0,
  ) ?? 0;

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group>
        <ThemeIcon size="lg" variant="gradient" gradient={{ from: 'blue', to: 'cyan' }}>
          <IconDatabase size={20} />
        </ThemeIcon>
        <div>
          <Title order={4}>AI Schema Import</Title>
          <Text size="xs" c="dimmed">
            Describe what you need — AI suggests the right pattern and generates the schema
          </Text>
        </div>
      </Group>

      {/* Stepper */}
      <Stepper active={activeStep} size="sm">
        <Stepper.Step label="Describe" description="What do you need?" />
        <Stepper.Step label="Review" description="AI analysis" />
        <Stepper.Step label="Generate" description="Create schema" />
        <Stepper.Completed>
          <Alert color="green" icon={<IconCheck size={16} />} mt="md">
            Schema generated successfully. Close this dialog and select the new entity type.
          </Alert>
        </Stepper.Completed>
      </Stepper>

      <Divider />

      {/* Step 1: Describe Intent */}
      {activeStep === 0 && (
        <Card withBorder p="lg">
          <Stack>
            <Textarea
              label="Describe the form you need"
              placeholder='e.g., "I need a patient registration form with insurance verification and emergency contacts"'
              value={description}
              onChange={(e) => setDescription(e.currentTarget.value)}
              minRows={3}
              maxRows={6}
              autosize
            />
            <Group justify="flex-end">
              {onCancel && (
                <Button variant="subtle" onClick={onCancel}>Cancel</Button>
              )}
              <Button
                onClick={handleAnalyze}
                loading={analyzing}
                disabled={!description.trim()}
                leftSection={<IconRobot size={16} />}
              >
                Analyze with AI
              </Button>
            </Group>
          </Stack>
        </Card>
      )}

      {/* Step 2: Review Analysis */}
      {activeStep === 1 && analysis && (
        <Stack gap="md">
          <Card withBorder p="lg">
            <Stack gap="md">
              <Text size="sm" fw={600}>AI Analysis</Text>

              <SimpleGrid cols={2}>
                <div>
                  <Text size="xs" c="dimmed">Subgraph</Text>
                  <Badge size="lg" variant="light">{analysis.subgraph}</Badge>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Entity Type</Text>
                  <Text fw={600}>{analysis.entityType}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Pattern</Text>
                  <Badge size="lg" color="violet" variant="light">
                    {analysis.suggestedPattern}
                  </Badge>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Confidence</Text>
                  <Text fw={600} c={analysis.confidence >= 0.7 ? 'green' : analysis.confidence >= 0.5 ? 'yellow' : 'red'}>
                    {(analysis.confidence * 100).toFixed(0)}%
                  </Text>
                </div>
              </SimpleGrid>

              {analysis.complianceFrameworks.length > 0 && (
                <div>
                  <Text size="xs" c="dimmed" mb={4}>Compliance Frameworks</Text>
                  <Group gap="xs">
                    {analysis.complianceFrameworks.map((f) => (
                      <Badge key={f} size="sm" color="green" variant="light">{f}</Badge>
                    ))}
                  </Group>
                </div>
              )}

              <Alert color="blue" icon={<IconRobot size={14} />}>
                <Text size="xs">{analysis.reasoning}</Text>
              </Alert>
            </Stack>
          </Card>

          {/* Pattern Template Preview */}
          {(loadingTemplate || template) && (
            <Card withBorder p="lg">
              <Text size="sm" fw={600} mb="sm">
                Pattern Preview: {template?.name ?? 'Loading...'}
              </Text>
              {template && (
                <Stack gap="sm">
                  <Text size="xs" c="dimmed">{template.description}</Text>
                  <Text size="xs" fw={500}>{totalFields} fields across {template.sections.length} section(s)</Text>
                  {template.sections.map((section) => (
                    <Paper key={section.title} p="xs" withBorder>
                      <Text size="xs" fw={600} mb={4}>{section.title}</Text>
                      <SimpleGrid cols={3}>
                        {section.fields.map((field) => (
                          <Group key={field.fieldName} gap={4}>
                            <Text size="xs">{field.label}</Text>
                            {field.required && <Badge size="xs" color="red">req</Badge>}
                          </Group>
                        ))}
                      </SimpleGrid>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Card>
          )}

          {/* Low confidence warning */}
          {analysis.confidence < 0.6 && (
            <Alert color="yellow" icon={<IconAlertCircle size={14} />}>
              <Text size="xs">
                Confidence is below 60%. The suggested pattern may not be ideal.
                You can still generate and customize afterwards.
              </Text>
            </Alert>
          )}

          <Group justify="flex-end">
            <Button variant="subtle" onClick={handleReset}>Start Over</Button>
            <Button
              onClick={handleGenerate}
              loading={generating}
              disabled={!template}
              leftSection={<IconSparkles size={16} />}
              color="green"
            >
              Generate Schema
            </Button>
          </Group>
        </Stack>
      )}

      {/* Step 3: Done */}
      {activeStep === 3 && (
        <Stack gap="md" align="center">
          <ThemeIcon size="xl" color="green" variant="light">
            <IconCheck size={28} />
          </ThemeIcon>
          <Text ta="center">
            FormSchema for <Code>{analysis?.entityType}</Code> has been created
            in the <Code>{analysis?.subgraph}</Code> subgraph.
          </Text>
          <Group>
            <Button variant="subtle" onClick={handleReset}>
              Import Another
            </Button>
            {onCancel && (
              <Button onClick={onCancel}>
                Close
              </Button>
            )}
          </Group>
        </Stack>
      )}
    </Stack>
  );
}
